/* California High UGC rail: desktop arrows, lightbox, analytics hooks.
   No dependencies. The rail itself scrolls natively (CSS scroll-snap); JS never re-implements scrolling.
   Custom element so rails re-inserted by facets.js (collection filter/sort) initialise themselves. */
(function () {
  'use strict';
  if (window.customElements.get('ch-ugc-rail')) return;

  var BUFFER_MAX = 200;
  var DEBUG = /[?&]ch_ugc_debug=1/.test(window.location.search);
  window.chUgcEvents = window.chUgcEvents || [];

  function track(name, detail) {
    var payload = Object.assign({ event: name, ts: Date.now() }, detail);
    try {
      window.chUgcEvents.push(payload);
      if (window.chUgcEvents.length > BUFFER_MAX) window.chUgcEvents.shift();
      document.dispatchEvent(new CustomEvent('ch:ugc', { detail: payload }));
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(name, payload);
      }
      if (Array.isArray(window.dataLayer)) window.dataLayer.push(payload);
      if (DEBUG) console.debug('[ch-ugc]', JSON.stringify(payload));
    } catch (e) { /* analytics must never break the page */ }
  }

  function cardData(card) {
    var d = card.dataset;
    return {
      index: Number(d.index),
      asset: d.asset || '',
      creator: d.creator || '',
      full: d.full,
      fullSrcset: d.fullSrcset,
      w: Number(d.fullW) || undefined,
      h: Number(d.fullH) || undefined,
      alt: d.alt || '',
      size: d.size || '',
      quote: d.quote || '',
      link: d.link || '',
      linkTitle: d.linkTitle || ''
    };
  }

  class ChUgcRail extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this.rail = this.querySelector('[data-ugc-rail]');
      this.cards = Array.prototype.slice.call(this.querySelectorAll('[data-ugc-card]'));
      this.prevBtn = this.querySelector('[data-ugc-prev]');
      this.nextBtn = this.querySelector('[data-ugc-next]');
      this.lb = this.querySelector('[data-ugc-lightbox]');
      this.ctx = {
        page_type: this.dataset.context || '',
        product: this.dataset.product || '',
        collection: this.dataset.collection || ''
      };
      this.current = -1;
      this.seen = {};
      if (!this.rail || !this.cards.length) return;
      this.setupArrows();
      this.setupCards();
      this.setupLightbox();
      this.setupObservers();
      this.setupDeferredImages();
    }

    /* ---------- deferred images: cards beyond the eager set load one card ahead of the scroll edge ----------
       Native loading="lazy" fetches everything within ~1250px, which on a phone is the whole rail. */
    setupDeferredImages() {
      var self = this;
      var pending = this.cards.map(function (c) { return c.querySelector('img[data-src]'); }).filter(Boolean);
      if (!pending.length) return;
      var reveal = function (img) {
        if (!img || !img.dataset.src) return;
        if (img.dataset.srcset) img.srcset = img.dataset.srcset;
        img.src = img.dataset.src;
        delete img.dataset.src;
        delete img.dataset.srcset;
      };
      if (!('IntersectionObserver' in window)) { pending.forEach(reveal); return; }
      var cardW = function () { return self.cards[0].getBoundingClientRect().width || 300; };
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          reveal(e.target.querySelector('img[data-src]'));
          io.unobserve(e.target);
        });
      }, { root: this.rail, rootMargin: '0px ' + Math.round(cardW() * 1.25) + 'px 0px 0px', threshold: 0.01 });
      pending.forEach(function (img) { io.observe(img.closest('[data-ugc-card]')); });
      /* Safety net: when the whole rail is near the viewport for a while, or on any rail interaction, load everything. */
      var loadAll = function () { pending.forEach(reveal); io.disconnect(); };
      this.rail.addEventListener('scroll', loadAll, { once: true, passive: true });
      if (this.nextBtn) this.nextBtn.addEventListener('click', loadAll, { once: true });
      if (this.prevBtn) this.prevBtn.addEventListener('click', loadAll, { once: true });
      var railIo = new IntersectionObserver(function (entries) {
        if (entries.some(function (e) { return e.isIntersecting; })) { setTimeout(loadAll, 4000); railIo.disconnect(); }
      }, { threshold: 0.25 });
      railIo.observe(this.rail);
    }

    /* ---------- rail arrows (desktop) ---------- */
    setupArrows() {
      var self = this;
      if (!this.prevBtn || !this.nextBtn) return;
      var update = function () {
        var canScroll = self.rail.scrollWidth - self.rail.clientWidth > 8;
        self.prevBtn.hidden = !canScroll;
        self.nextBtn.hidden = !canScroll;
        if (!canScroll) return;
        var x = self.rail.scrollLeft;
        self.prevBtn.disabled = x <= 2;
        self.nextBtn.disabled = x + self.rail.clientWidth >= self.rail.scrollWidth - 2;
      };
      var step = function () {
        var card = self.cards[0];
        var gap = parseFloat(getComputedStyle(self.rail).columnGap || getComputedStyle(self.rail).gap) || 12;
        return card.getBoundingClientRect().width + gap;
      };
      this.prevBtn.addEventListener('click', function () {
        self.rail.scrollBy({ left: -step() * 2, behavior: 'smooth' });
        track('ugc_navigate', Object.assign({ direction: 'prev', surface: 'rail' }, self.ctx));
      });
      this.nextBtn.addEventListener('click', function () {
        self.rail.scrollBy({ left: step() * 2, behavior: 'smooth' });
        track('ugc_navigate', Object.assign({ direction: 'next', surface: 'rail' }, self.ctx));
      });
      var raf = null;
      this.rail.addEventListener('scroll', function () {
        if (raf) return;
        raf = requestAnimationFrame(function () { raf = null; update(); });
      }, { passive: true });
      window.addEventListener('resize', update, { passive: true });
      update();
    }

    /* ---------- cards ---------- */
    setupCards() {
      var self = this;
      this.cards.forEach(function (card, i) {
        var open = card.querySelector('[data-ugc-open]');
        if (open) open.addEventListener('click', function () { self.open(i, open); });
        var link = card.querySelector('[data-ugc-product]');
        if (link) link.addEventListener('click', function () {
          var d = cardData(card);
          track('ugc_product_click', Object.assign({
            ugc_asset: d.asset, position: d.index, creator: d.creator,
            destination_product: link.getAttribute('href'), surface: 'card'
          }, self.ctx));
        });
      });
    }

    /* ---------- observers: rail visible + per-card impressions ---------- */
    setupObservers() {
      var self = this;
      if (!('IntersectionObserver' in window)) return;
      var railSeen = false;
      var railObs = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting && !railSeen) {
            railSeen = true;
            track('ugc_rail_visible', Object.assign({ count: self.cards.length }, self.ctx));
            railObs.disconnect();
          }
        });
      }, { threshold: 0.5 });
      railObs.observe(this.rail);

      var timers = {};
      var cardObs = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          var card = e.target;
          var idx = card.dataset.index;
          if (self.seen[idx]) return;
          if (e.isIntersecting && e.intersectionRatio >= 0.5) {
            timers[idx] = setTimeout(function () {
              self.seen[idx] = true;
              var d = cardData(card);
              track('ugc_impression', Object.assign({ ugc_asset: d.asset, position: d.index, creator: d.creator }, self.ctx));
              cardObs.unobserve(card);
            }, 500);
          } else if (timers[idx]) {
            clearTimeout(timers[idx]);
            timers[idx] = null;
          }
        });
      }, { threshold: [0, 0.5, 1] });
      this.cards.forEach(function (c) { cardObs.observe(c); });
    }

    /* ---------- lightbox ---------- */
    setupLightbox() {
      var self = this;
      var lb = this.lb;
      if (!lb) return;
      this.lbImg = lb.querySelector('[data-ugc-lb-img]');
      this.lbMeta = lb.querySelector('[data-ugc-lb-meta]');
      this.lbQuote = lb.querySelector('[data-ugc-lb-quote]');
      this.lbLink = lb.querySelector('[data-ugc-lb-link]');
      this.lbStatus = lb.querySelector('[data-ugc-lb-status]');
      this.lbPrev = lb.querySelector('[data-ugc-lb-prev]');
      this.lbNext = lb.querySelector('[data-ugc-lb-next]');
      this.supportsDialog = typeof lb.showModal === 'function';

      lb.querySelector('[data-ugc-close]').addEventListener('click', function () { self.close('button'); });
      this.lbPrev.addEventListener('click', function () { self.step(-1, 'button'); });
      this.lbNext.addEventListener('click', function () { self.step(1, 'button'); });
      this.lbLink.addEventListener('click', function () {
        var d = cardData(self.cards[self.current]);
        track('ugc_product_click', Object.assign({
          ugc_asset: d.asset, position: d.index, creator: d.creator,
          destination_product: self.lbLink.getAttribute('href'), surface: 'lightbox'
        }, self.ctx));
      });

      /* backdrop click: the dialog element itself is the backdrop area outside the frame */
      lb.addEventListener('click', function (e) {
        if (e.target === lb || e.target === lb.querySelector('[data-ugc-lb-frame]')) self.close('backdrop');
      });
      lb.addEventListener('cancel', function (e) { e.preventDefault(); self.close('escape'); });
      lb.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowLeft') { e.preventDefault(); self.step(-1, 'keyboard'); }
        else if (e.key === 'ArrowRight') { e.preventDefault(); self.step(1, 'keyboard'); }
        else if (e.key === 'Escape' && !self.supportsDialog) { self.close('escape'); }
      });

      /* swipe: native scroll cannot drive a single-image dialog, so a light pointer handler */
      var startX = null, startY = null;
      lb.addEventListener('pointerdown', function (e) { startX = e.clientX; startY = e.clientY; }, { passive: true });
      lb.addEventListener('pointerup', function (e) {
        if (startX === null) return;
        var dx = e.clientX - startX, dy = e.clientY - startY;
        startX = startY = null;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.5) self.step(dx < 0 ? 1 : -1, 'swipe');
      }, { passive: true });
    }

    render(i) {
      var d = cardData(this.cards[i]);
      var n = this.cards.length;
      this.current = i;
      this.lbImg.src = d.full;
      if (d.fullSrcset) this.lbImg.srcset = d.fullSrcset;
      if (d.w) this.lbImg.width = d.w;
      if (d.h) this.lbImg.height = d.h;
      this.lbImg.alt = d.alt;
      var meta = [];
      if (d.creator) meta.push('@' + d.creator);
      if (d.size) meta.push('Size ' + d.size);
      meta.push(i + 1 + ' / ' + n);
      this.lbMeta.textContent = meta.join('  ·  ');
      this.lbQuote.hidden = !d.quote;
      this.lbQuote.textContent = d.quote ? '“' + d.quote + '”' : '';
      if (d.link) {
        this.lbLink.hidden = false;
        this.lbLink.href = d.link;
        this.lbLink.textContent = d.linkTitle || 'Shop this look';
      } else {
        this.lbLink.hidden = true;
      }
      this.lbPrev.disabled = i === 0;
      this.lbNext.disabled = i === n - 1;
      this.lbStatus.textContent = 'Photo ' + (i + 1) + ' of ' + n + (d.alt ? ': ' + d.alt : '');
      /* warm the neighbours */
      [i - 1, i + 1].forEach(function (j) {
        if (j < 0 || j >= n) return;
        var img = new Image();
        img.src = cardData(this.cards[j]).full;
      }, this);
    }

    open(i, opener) {
      if (!this.lb) return;
      this.opener = opener || null;
      var cimg = this.cards[i].querySelector('img[data-src]');
      if (cimg) { if (cimg.dataset.srcset) cimg.srcset = cimg.dataset.srcset; cimg.src = cimg.dataset.src; delete cimg.dataset.src; delete cimg.dataset.srcset; }
      this.render(i);
      document.body.classList.add('ch-ugc-lb-open');
      if (this.supportsDialog) {
        if (!this.lb.open) this.lb.showModal();
      } else {
        this.lb.setAttribute('open', '');
        this.lb.style.position = 'fixed';
        this.lb.style.inset = '0';
        this.lb.style.zIndex = '1000';
        this.lb.style.background = 'rgba(33,16,25,.92)';
      }
      var closeBtn = this.lb.querySelector('[data-ugc-close]');
      if (closeBtn) closeBtn.focus();
      var d = cardData(this.cards[i]);
      track('ugc_open', Object.assign({ ugc_asset: d.asset, position: d.index, creator: d.creator }, this.ctx));
    }

    step(dir, method) {
      var j = this.current + dir;
      if (j < 0 || j >= this.cards.length) return;
      var from = cardData(this.cards[this.current]);
      this.render(j);
      var to = cardData(this.cards[j]);
      track('ugc_navigate', Object.assign({
        direction: dir > 0 ? 'next' : 'prev', surface: 'lightbox', method: method,
        from_asset: from.asset, to_asset: to.asset, ugc_asset: to.asset, position: to.index, creator: to.creator
      }, this.ctx));
      /* keep the rail in step so closing lands on the same card */
      var card = this.cards[j];
      if (card && card.scrollIntoView) card.scrollIntoView({ block: 'nearest', inline: 'start', behavior: 'auto' });
    }

    close(method) {
      if (!this.lb) return;
      document.body.classList.remove('ch-ugc-lb-open');
      if (this.supportsDialog) { if (this.lb.open) this.lb.close(); }
      else { this.lb.removeAttribute('open'); }
      this.lbImg.removeAttribute('src');
      this.lbImg.removeAttribute('srcset');
      var target = this.cards[this.current] && this.cards[this.current].querySelector('[data-ugc-open]');
      if (target) target.focus({ preventScroll: false });
      else if (this.opener) this.opener.focus();
      track('ugc_close', Object.assign({ method: method }, this.ctx));
    }
  }

  window.customElements.define('ch-ugc-rail', ChUgcRail);
})();
